/**
 * Interpreter services (บริการล่าม) — cluster source of truth.
 *
 * Powers /interpreter, /interpreter/service/$service, /interpreter/questions
 * and /interpreter/q/$slug.
 *
 * FACTUAL GUARDRAILS
 * - No prices anywhere (SHOW_PUBLIC_PRICES=false). Rates depend on language
 *   pair, venue, duration and travel, so every CTA points to phone/LINE/email.
 * - Statutory statements are limited to provisions we can cite:
 *   ประมวลกฎหมายวิธีพิจารณาความอาญา มาตรา 13 (การจัดหาล่ามเมื่อผู้เกี่ยวข้อง
 *   ไม่เข้าใจภาษาไทย) and ประมวลกฎหมายวิธีพิจารณาความแพ่ง มาตรา 46
 *   (ศาลใช้ภาษาไทย / การแปลเอกสารภาษาต่างประเทศ). Anything operational —
 *   whether a specific ที่ว่าการอำเภอ or สถานทูต accepts a given interpreter —
 *   is written as "ต้องยืนยันกับหน่วยงานผู้รับ" rather than asserted.
 * - Turnaround, availability and acceptance are never guaranteed.
 */

export const INTERPRETER_REVIEWED_TH = "สิงหาคม 2569";
export const INTERPRETER_REVIEWED_ISO = "2026-08-04T00:00:00.000Z";

export type InterpreterMode = "onsite" | "remote" | "both";

export type InterpreterScenario = {
  slug: string;
  th: string;
  en: string;
  /** Where the assignment normally takes place. */
  venueTh: string;
  mode: InterpreterMode;
  /** 50–80 word answer-first summary. */
  summaryTh: string;
  /** What the receiving authority typically expects. */
  requirementsTh: string[];
  stepsTh: { title: string; detail: string }[];
  tipsTh: string[];
  warningsTh: string[];
  /** Authority / legal reference shown as a citation line. */
  citationTh: string;
};

export const INTERPRETER_LANGUAGES: readonly { slug: string; th: string; en: string }[] = [
  { slug: "english", th: "อังกฤษ", en: "English" },
  { slug: "chinese", th: "จีน (กลาง)", en: "Chinese (Mandarin)" },
  { slug: "japanese", th: "ญี่ปุ่น", en: "Japanese" },
  { slug: "korean", th: "เกาหลี", en: "Korean" },
  { slug: "vietnamese", th: "เวียดนาม", en: "Vietnamese" },
  { slug: "indonesian", th: "อินโดนีเซีย", en: "Indonesian" },
  { slug: "malay", th: "มาเลย์", en: "Malay" },
  { slug: "myanmar", th: "เมียนมา", en: "Burmese" },
  { slug: "khmer", th: "เขมร", en: "Khmer" },
  { slug: "lao", th: "ลาว", en: "Lao" },
  { slug: "hindi", th: "ฮินดี", en: "Hindi" },
  { slug: "arabic", th: "อาหรับ", en: "Arabic" },
  { slug: "russian", th: "รัสเซีย", en: "Russian" },
  { slug: "german", th: "เยอรมัน", en: "German" },
  { slug: "french", th: "ฝรั่งเศส", en: "French" },
  { slug: "spanish", th: "สเปน", en: "Spanish" },
  { slug: "italian", th: "อิตาลี", en: "Italian" },
  { slug: "portuguese", th: "โปรตุเกส", en: "Portuguese" },
  { slug: "dutch", th: "ดัตช์", en: "Dutch" },
  { slug: "swedish", th: "สวีเดน", en: "Swedish" },
] as const;

export const INTERPRETER_MODE_LABEL: Record<InterpreterMode, string> = {
  onsite: "ล่ามในสถานที่",
  remote: "ล่ามทางไกล (VRI/OPI)",
  both: "เลือกได้ทั้งในสถานที่และทางไกล",
};

const COMMON_TIPS = [
  "ส่งเอกสารคดี/สัญญา/ศัพท์เฉพาะให้ล่ามล่วงหน้าอย่างน้อย 1–2 วันทำการ เพื่อเตรียมคำศัพท์และชื่อเฉพาะให้ตรงกัน",
  "แจ้งภาษาถิ่นหรือสำเนียงที่คู่สนทนาใช้จริง (เช่น จีนกลาง/กวางตุ้ง, อาหรับถิ่น) เพราะเป็นคนละทักษะกัน",
  "ยืนยันกับหน่วยงานผู้รับก่อนวันนัดว่าต้องการล่ามที่มีคุณสมบัติแบบใด และต้องแสดงบัตรประชาชน/หนังสือเดินทางของล่ามหรือไม่",
  "เผื่อเวลาเดินทางและขั้นตอนคัดกรองหน้าอาคารราชการ โดยเฉพาะศาลและสำนักงานตรวจคนเข้าเมือง",
  "ถ้าต้องใช้เอกสารประกอบเป็นภาษาต่างประเทศ ให้เตรียมคำแปลภาษาไทยไปพร้อมกัน เพราะหน่วยงานราชการไทยใช้ภาษาไทยเป็นหลัก",
];

const COMMON_WARNINGS = [
  "อย่าให้ญาติหรือคู่กรณีทำหน้าที่ล่าม — หลายหน่วยงานถือว่ามีส่วนได้เสียและอาจไม่รับ",
  "อย่าคาดว่าหน่วยงานจะจัดล่ามภาษาที่หายากให้ทันในวันเดียว ควรนัดหมายล่วงหน้า",
  "อย่าใช้แอปแปลภาษาแทนล่ามในกระบวนการที่มีผลทางกฎหมาย เพราะอาจถูกโต้แย้งภายหลัง",
  "อย่าสรุปหรือย่อความแทนคู่ความ — ล่ามต้องแปลตามที่พูดจริง ผู้ว่าจ้างไม่ควรร้องขอให้ตัดทอน",
  "อย่าลืมตรวจการสะกดชื่อ-สกุลในเอกสารให้ตรงกับหนังสือเดินทาง มิฉะนั้นเอกสารที่ทำในวันนั้นอาจถูกตีกลับ",
  "อย่ายืนยันวันนัดกับหน่วยงานก่อนที่ล่ามจะยืนยันคิว เพราะการเลื่อนนัดราชการมักเสียเวลาหลายสัปดาห์",
];

export const INTERPRETER_SCENARIOS: readonly InterpreterScenario[] = [
  {
    slug: "court",
    th: "ล่ามศาลและกระบวนพิจารณาคดี",
    en: "Court interpreting",
    venueTh: "ศาลยุติธรรม / ห้องพิจารณาคดี",
    mode: "onsite",
    summaryTh:
      "ล่ามศาลทำหน้าที่แปลคำเบิกความ คำถามค้าน และคำสั่งศาลให้คู่ความที่ไม่เข้าใจภาษาไทย กฎหมายไทยกำหนดให้ศาลใช้ภาษาไทยเป็นหลัก และให้จัดหาล่ามเมื่อผู้เกี่ยวข้องไม่เข้าใจภาษาไทย เราจัดล่ามพร้อมเอกสารแนะนำตัวและประวัติประสบการณ์ให้ศาลพิจารณา และประสานคำศัพท์คดีล่วงหน้ากับทนายความผู้รับผิดชอบ",
    requirementsTh: [
      "หมายนัด/เลขคดีและชื่อศาล เพื่อจองคิวล่ามให้ตรงห้องพิจารณา",
      "สำเนาคำฟ้อง คำให้การ หรือเอกสารสำคัญ เพื่อเตรียมศัพท์เฉพาะ",
      "หนังสือเดินทางหรือบัตรประจำตัวของคู่ความชาวต่างชาติ",
      "การยืนยันจากศาลว่าอนุญาตให้ใช้ล่ามที่คู่ความจัดหาเอง",
    ],
    stepsTh: [
      { title: "แจ้งข้อมูลคดีและวันนัด", detail: "ระบุภาษา ประเภทคดี ศาล วันเวลานัด และจำนวนชั่วโมงที่คาดว่าจะใช้" },
      { title: "จับคู่ล่ามและเตรียมศัพท์", detail: "ส่งประวัติล่ามให้ตรวจ และส่งเอกสารคดีให้ล่ามเตรียมคำศัพท์เฉพาะทาง" },
      { title: "ยืนยันกับศาล", detail: "ทนายความยื่นคำแถลงขอใช้ล่ามหรือแจ้งเจ้าหน้าที่หน้าบัลลังก์ตามแนวปฏิบัติของศาลนั้น" },
      { title: "ปฏิบัติงานวันนัด", detail: "ล่ามรายงานตัวก่อนเวลา สาบานตนตามที่ศาลกำหนด และแปลแบบต่อเนื่องในห้องพิจารณา" },
      { title: "สรุปหลังนัด", detail: "บันทึกคำศัพท์ที่ใช้และประเด็นที่ต้องเตรียมเพิ่ม เพื่อความต่อเนื่องในนัดถัดไป" },
    ],
    tipsTh: [
      "แจ้งชื่อพยานและลำดับการสืบพยานล่วงหน้า ล่ามจะเตรียมชื่อเฉพาะและศัพท์เทคนิคได้ตรงกว่า",
    ],
    warningsTh: [
      "อย่าให้ล่ามอธิบายหรือให้ความเห็นทางกฎหมายแทนทนายความ — เกินขอบเขตหน้าที่ล่าม",
    ],
    citationTh:
      "อ้างอิง: ประมวลกฎหมายวิธีพิจารณาความอาญา มาตรา 13 (การจัดหาล่าม) และประมวลกฎหมายวิธีพิจารณาความแพ่ง มาตรา 46 (ศาลใช้ภาษาไทย/การแปลเอกสาร)",
  },
  {
    slug: "police",
    th: "ล่ามชั้นสอบสวนและสถานีตำรวจ",
    en: "Police & investigation interpreting",
    venueTh: "สถานีตำรวจ / สำนักงานพนักงานสอบสวน",
    mode: "onsite",
    summaryTh:
      "เมื่อผู้ต้องหา ผู้เสียหาย หรือพยานไม่เข้าใจภาษาไทย พนักงานสอบสวนต้องจัดให้มีล่ามในการสอบปากคำตามประมวลกฎหมายวิธีพิจารณาความอาญา ล่ามของเราแปลคำถาม-คำตอบแบบต่อเนื่อง ช่วยให้บันทึกคำให้การตรงกับสิ่งที่ผู้ให้ถ้อยคำพูดจริง และแปลเนื้อหาบันทึกก่อนลงลายมือชื่อ",
    requirementsTh: [
      "ชื่อสถานีตำรวจ ชื่อพนักงานสอบสวน และเวลานัดสอบปากคำ",
      "หนังสือเดินทาง/เอกสารประจำตัวของผู้ให้ถ้อยคำ",
      "ข้อมูลข้อกล่าวหาหรือลักษณะเหตุการณ์โดยย่อ",
    ],
    stepsTh: [
      { title: "รับแจ้งเหตุและภาษา", detail: "แจ้งภาษา สถานที่ และกรอบเวลา เพื่อจัดล่ามที่พร้อมเดินทาง" },
      { title: "ประสานพนักงานสอบสวน", detail: "ยืนยันว่าพนักงานสอบสวนอนุญาตให้ใช้ล่ามที่จัดหามาเอง หรือใช้ล่ามที่ทางราชการจัดให้" },
      { title: "สอบปากคำ", detail: "ล่ามแปลคำถามและคำตอบตามจริง ไม่เพิ่มไม่ตัด และขอให้หยุดเป็นช่วงเพื่อความแม่นยำ" },
      { title: "ทวนบันทึกก่อนลงชื่อ", detail: "แปลบันทึกคำให้การให้ผู้ให้ถ้อยคำฟังทั้งฉบับก่อนลงลายมือชื่อ" },
    ],
    tipsTh: [
      "แจ้งล่ามหากมีศัพท์ทางการแพทย์ บัญชี หรือเทคนิคในคดี เพื่อเตรียมคำศัพท์",
      "ขอให้พนักงานสอบสวนพูดเป็นช่วงสั้น ๆ จะได้คำแปลที่ครบถ้วนกว่า",
    ],
    warningsTh: [
      "อย่าลงลายมือชื่อในบันทึกที่ยังไม่ได้ฟังคำแปลครบทั้งฉบับ",
    ],
    citationTh: "อ้างอิง: ประมวลกฎหมายวิธีพิจารณาความอาญา มาตรา 13",
  },
  {
    slug: "marriage",
    th: "ล่ามจดทะเบียนสมรสที่อำเภอ/สำนักงานเขต",
    en: "Marriage registration interpreting",
    venueTh: "ที่ว่าการอำเภอ / สำนักงานเขต",
    mode: "onsite",
    summaryTh:
      "การจดทะเบียนสมรสกับชาวต่างชาติที่ที่ว่าการอำเภอหรือสำนักงานเขต นายทะเบียนต้องมั่นใจว่าคู่สมรสเข้าใจข้อความในทะเบียนสมรสและให้ความยินยอมโดยสมัครใจ หลายพื้นที่จึงขอให้มีล่ามและพยานร่วมด้วย ล่ามของเราแปลแบบฟอร์ม คำถามของนายทะเบียน และข้อตกลงก่อนสมรส (ถ้ามี) ให้ครบก่อนลงลายมือชื่อ",
    requirementsTh: [
      "หนังสือรับรองสถานภาพการสมรสจากสถานทูตของคู่สมรสต่างชาติ พร้อมคำแปลไทยที่ผ่านนิติกรณ์กรมการกงสุล",
      "หนังสือเดินทางและบัตรประชาชน/ทะเบียนบ้านของทั้งสองฝ่าย",
      "พยานตามที่นายทะเบียนกำหนด",
      "การยืนยันล่วงหน้ากับสำนักทะเบียนนั้นว่ารับล่ามที่คู่สมรสจัดหาเองหรือไม่",
    ],
    stepsTh: [
      { title: "เตรียมเอกสารต้นทาง", detail: "ขอหนังสือรับรองโสด/สถานภาพจากสถานทูต แล้วแปลและผ่านนิติกรณ์กรมการกงสุล" },
      { title: "นัดหมายสำนักทะเบียน", detail: "โทรยืนยันวันเวลาที่รับจดทะเบียนสำหรับคู่สมรสต่างชาติ และเงื่อนไขเรื่องล่าม" },
      { title: "จัดล่ามและพยาน", detail: "ยืนยันภาษา จำนวนชั่วโมง และเอกสารประจำตัวของล่ามตามที่นายทะเบียนขอ" },
      { title: "วันจดทะเบียน", detail: "ล่ามแปลคำถามนายทะเบียน แบบ คร.2 และข้อความในทะเบียนสมรสก่อนลงลายมือชื่อ" },
    ],
    tipsTh: [
      "ตรวจการสะกดชื่อในคำแปลให้ตรงหนังสือเดินทางทุกตัวอักษรก่อนถึงวันนัด",
      "ถ้าจะทำสัญญาก่อนสมรส ควรจัดเตรียมและให้ล่ามอ่านให้ฟังก่อนวันจดทะเบียน",
    ],
    warningsTh: [
      "อย่าใช้หนังสือรับรองสถานภาพที่ยังไม่ผ่านนิติกรณ์กรมการกงสุล — นายทะเบียนมักไม่รับ",
      "อย่าคาดว่าทุกสำนักทะเบียนมีแนวปฏิบัติเหมือนกัน ควรโทรยืนยันเป็นรายแห่ง",
    ],
    citationTh:
      "อ้างอิง: ประมวลกฎหมายแพ่งและพาณิชย์ บรรพ 5 ว่าด้วยการสมรส และแนวปฏิบัติของสำนักทะเบียนแต่ละแห่ง (ยืนยันกับสำนักทะเบียนก่อนวันนัด)",
  },
  {
    slug: "immigration",
    th: "ล่ามสำนักงานตรวจคนเข้าเมืองและงานวีซ่า",
    en: "Immigration interpreting",
    venueTh: "สำนักงานตรวจคนเข้าเมือง / ศูนย์บริการวีซ่า",
    mode: "both",
    summaryTh:
      "งานตรวจคนเข้าเมือง เช่น การสัมภาษณ์ขอวีซ่าติดตามครอบครัว การต่ออายุการพำนัก หรือการชี้แจงกรณีอยู่เกินกำหนด มักต้องตอบคำถามเจ้าหน้าที่เป็นภาษาไทย ล่ามช่วยให้ผู้ยื่นเข้าใจคำถามและตอบได้ตรงประเด็น พร้อมอธิบายรายการเอกสารที่เจ้าหน้าที่ขอเพิ่ม",
    requirementsTh: [
      "หนังสือเดินทางและใบ ตม.6 / ใบอนุญาตพำนักปัจจุบัน",
      "เอกสารประกอบตามประเภทคำขอ (การเงิน ที่พัก งาน ครอบครัว)",
      "แบบคำขอที่กรอกล่วงหน้า เพื่อให้ล่ามตรวจทานคำแปล",
    ],
    stepsTh: [
      { title: "ระบุประเภทคำขอ", detail: "แจ้งว่าเป็นการต่ออายุ เปลี่ยนประเภท สัมภาษณ์ครอบครัว หรือชี้แจงกรณีพิเศษ" },
      { title: "ตรวจเอกสารล่วงหน้า", detail: "ทวนรายการเอกสารและคำแปลที่ต้องใช้ก่อนวันนัด" },
      { title: "เข้าพบเจ้าหน้าที่", detail: "ล่ามแปลคำถาม-คำตอบและรายการเอกสารที่เจ้าหน้าที่ขอเพิ่มเติม" },
      { title: "ติดตามผล", detail: "สรุปสิ่งที่ต้องยื่นเพิ่มและกำหนดวันนัดครั้งถัดไปเป็นลายลักษณ์อักษร" },
    ],
    tipsTh: [
      "ถ่ายสำเนาเอกสารทุกฉบับเผื่อไว้ เจ้าหน้าที่มักขอชุดสำเนาเพิ่มหน้างาน",
      "ไปถึงก่อนเวลาอย่างน้อย 45 นาทีในช่วงต้นเดือนและปลายเดือนซึ่งคิวยาว",
    ],
    warningsTh: [
      "อย่าให้ล่ามตอบแทนผู้ยื่น — เจ้าหน้าที่ต้องได้ยินคำตอบจากผู้ยื่นเอง",
      "อย่าให้ข้อมูลที่ไม่ตรงกับเอกสารที่ยื่นไว้ก่อนหน้า",
    ],
    citationTh:
      "อ้างอิง: พระราชบัญญัติคนเข้าเมือง พ.ศ. 2522 และประกาศ/ระเบียบของสำนักงานตรวจคนเข้าเมืองที่ใช้บังคับ ณ วันยื่น",
  },
  {
    slug: "notary",
    th: "ล่ามประกอบการรับรองลายมือชื่อโดยทนายความ (Notarial Services Attorney)",
    en: "Notarial signing interpreting",
    venueTh: "สำนักงานทนายความ / สถานที่ลงนาม",
    mode: "both",
    summaryTh:
      "ก่อนลงลายมือชื่อในหนังสือมอบอำนาจ คำให้การ (Affidavit) หรือเอกสารที่ต้องให้ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารรับรอง ผู้ลงนามต้องเข้าใจข้อความที่ตนลงนาม ล่ามจะอ่านและแปลเอกสารทั้งฉบับให้ฟัง เพื่อให้การรับรองมีความน่าเชื่อถือและลดข้อโต้แย้งเรื่องความยินยอมในภายหลัง",
    requirementsTh: [
      "ร่างเอกสารฉบับสมบูรณ์ล่วงหน้า เพื่อให้ล่ามเตรียมคำแปล",
      "หนังสือเดินทางหรือบัตรประจำตัวของผู้ลงนาม",
      "ข้อกำหนดของหน่วยงานปลายทางว่าต้องมีถ้อยคำรับรองล่ามในเอกสารหรือไม่",
    ],
    stepsTh: [
      { title: "ส่งร่างเอกสาร", detail: "ส่งไฟล์เอกสารและระบุปลายทางที่จะใช้ เพื่อกำหนดรูปแบบคำรับรอง" },
      { title: "เตรียมคำแปล", detail: "ล่ามเตรียมคำแปลปากเปล่าและตรวจศัพท์กฎหมายร่วมกับทนายความ" },
      { title: "วันลงนาม", detail: "ล่ามแปลเอกสารให้ผู้ลงนามฟังต่อหน้าทนายความ แล้วจึงลงลายมือชื่อ" },
      { title: "ขั้นตอนต่อเนื่อง", detail: "หากต้องใช้ต่างประเทศ ดำเนินการนิติกรณ์กรมการกงสุลและรับรองสถานทูตปลายทางตามที่ผู้รับกำหนด" },
    ],
    tipsTh: [
      "ยืนยันกับหน่วยงานปลายทางว่าต้องการถ้อยคำรับรองของล่ามในตัวเอกสารหรือไม่",
      "เตรียมเอกสารเป็นสองภาษาแบบคอลัมน์คู่ ช่วยให้ตรวจสอบภายหลังง่ายขึ้น",
    ],
    warningsTh: [
      "อย่าลงลายมือชื่อก่อนที่ล่ามจะแปลครบทั้งฉบับต่อหน้าทนายความ",
      "อย่าแก้ไขข้อความด้วยลายมือหลังลงนาม — ควรพิมพ์ใหม่และลงนามใหม่",
    ],
    citationTh:
      "อ้างอิง: ระเบียบสภาทนายความว่าด้วยการขึ้นทะเบียนทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร",
  },
  {
    slug: "business",
    th: "ล่ามธุรกิจ ประชุม และเจรจาสัญญา",
    en: "Business & negotiation interpreting",
    venueTh: "สำนักงาน / โรงงาน / ห้องประชุม",
    mode: "both",
    summaryTh:
      "ล่ามธุรกิจรองรับการประชุมผู้ถือหุ้น การตรวจโรงงาน การเจรจาสัญญา และการอบรมพนักงาน โดยเลือกรูปแบบได้ทั้งล่ามพูดตาม (consecutive) สำหรับวงประชุมเล็ก และล่ามพูดพร้อม (simultaneous) สำหรับงานสัมมนาที่มีอุปกรณ์รองรับ เราขอวาระประชุมและศัพท์เฉพาะขององค์กรล่วงหน้าเพื่อความแม่นยำ",
    requirementsTh: [
      "วาระการประชุมและรายชื่อผู้เข้าร่วม",
      "เอกสารประกอบ เช่น ร่างสัญญา งบการเงิน หรือคู่มือเทคนิค",
      "รูปแบบล่ามที่ต้องการและระยะเวลา",
    ],
    stepsTh: [
      { title: "กำหนดขอบเขตงาน", detail: "ระบุภาษา รูปแบบล่าม จำนวนผู้ฟัง สถานที่ และช่วงเวลา" },
      { title: "เตรียมคำศัพท์", detail: "จัดทำ glossary ร่วมกับลูกค้า โดยเฉพาะชื่อผลิตภัณฑ์และตัวย่อภายในองค์กร" },
      { title: "ทดสอบระบบ", detail: "งานล่ามพูดพร้อมหรือออนไลน์ ควรทดสอบเสียงและช่องสัญญาณก่อนวันงาน" },
      { title: "ปฏิบัติงานและสรุป", detail: "หลังงานส่ง glossary ที่ปรับปรุงแล้วให้ใช้ต่อในการประชุมครั้งถัดไป" },
    ],
    tipsTh: [
      "งานล่ามพูดพร้อมเกิน 1 ชั่วโมงควรใช้ล่าม 2 คนสลับกัน เพื่อรักษาคุณภาพ",
      "ส่งสไลด์ล่วงหน้า แม้เป็นฉบับร่าง ก็ช่วยลดความคลาดเคลื่อนได้มาก",
    ],
    warningsTh: [
      "อย่าให้ล่ามทำหน้าที่ต่อรองแทนคุณ — ล่ามแปลสาร ไม่ใช่ตัวแทนเจรจา",
      "อย่าพูดทับกันหลายคน คำแปลจะตกหล่นทันที",
    ],
    citationTh: "แนวปฏิบัติวิชาชีพล่าม: ความเป็นกลาง ความลับ และการแปลครบถ้วนตามที่ผู้พูดสื่อสาร",
  },
  {
    slug: "medical",
    th: "ล่ามการแพทย์และโรงพยาบาล",
    en: "Medical interpreting",
    venueTh: "โรงพยาบาล / คลินิก",
    mode: "both",
    summaryTh:
      "ล่ามการแพทย์ช่วยสื่อสารระหว่างผู้ป่วยต่างชาติกับแพทย์ ตั้งแต่การซักประวัติ การอธิบายแผนการรักษา ไปจนถึงการลงนามในหนังสือแสดงความยินยอมรับการรักษา ล่ามต้องรักษาความลับผู้ป่วยและแปลตามจริงโดยไม่ให้ความเห็นทางการแพทย์",
    requirementsTh: [
      "ชื่อโรงพยาบาล แผนก และเวลานัดหมาย",
      "ประวัติการรักษาหรือใบส่งตัว (ถ้ามี) เพื่อเตรียมศัพท์",
      "เอกสารประกันสุขภาพหากต้องประสานเรื่องความคุ้มครอง",
    ],
    stepsTh: [
      { title: "แจ้งข้อมูลนัดหมาย", detail: "ระบุแผนก ภาษา และลักษณะการรักษาโดยย่อ" },
      { title: "เตรียมศัพท์การแพทย์", detail: "ล่ามทบทวนศัพท์เฉพาะโรคและชื่อยาที่เกี่ยวข้อง" },
      { title: "ร่วมพบแพทย์", detail: "แปลการซักประวัติ ผลตรวจ และคำอธิบายทางเลือกการรักษา" },
      { title: "เอกสารยินยอม", detail: "แปลหนังสือแสดงความยินยอมทั้งฉบับก่อนผู้ป่วยลงนาม" },
    ],
    tipsTh: [
      "เตรียมรายชื่อยาที่ใช้ประจำพร้อมชื่อสามัญทางยา จะลดความคลาดเคลื่อนได้มาก",
      "ขอให้แพทย์หยุดเป็นช่วงสั้น ๆ เพื่อให้คำแปลครบถ้วน",
    ],
    warningsTh: [
      "ล่ามไม่ให้คำแนะนำทางการแพทย์และไม่ตัดสินใจแทนผู้ป่วย",
      "อย่าใช้เด็กหรือญาติเป็นล่ามในเรื่องที่ละเอียดอ่อน",
    ],
    citationTh:
      "อ้างอิงหลักความยินยอมโดยได้รับข้อมูล (informed consent) และหน้าที่รักษาความลับผู้ป่วยตามมาตรฐานวิชาชีพเวชกรรม",
  },
  {
    slug: "remote",
    th: "ล่ามทางไกล VRI / OPI (วิดีโอและโทรศัพท์)",
    en: "Remote interpreting (VRI/OPI)",
    venueTh: "ออนไลน์ (วิดีโอคอล/โทรศัพท์)",
    mode: "remote",
    summaryTh:
      "ล่ามทางไกลเหมาะกับการประชุมสั้น การสัมภาษณ์ออนไลน์ และงานเร่งด่วนที่ไม่ต้องเดินทาง รองรับทั้งแบบวิดีโอ (VRI) และทางโทรศัพท์ (OPI) เราแนะนำให้ทดสอบสัญญาณและอุปกรณ์ก่อนเริ่มงาน และเตรียมเอกสารเป็นไฟล์ให้ทุกฝ่ายเปิดพร้อมกันเพื่อลดความคลาดเคลื่อน",
    requirementsTh: [
      "แพลตฟอร์มที่ใช้ (เช่น Zoom, Google Meet, MS Teams) และลิงก์เข้าร่วม",
      "รายชื่อผู้เข้าร่วมและภาษาที่ใช้",
      "เอกสารประกอบเป็นไฟล์ดิจิทัลล่วงหน้า",
    ],
    stepsTh: [
      { title: "จองช่วงเวลา", detail: "ระบุภาษา วันเวลา และความยาวการประชุม" },
      { title: "ทดสอบระบบ", detail: "ทดสอบไมโครโฟน หูฟัง และความเสถียรของสัญญาณก่อนเวลานัด 10–15 นาที" },
      { title: "ดำเนินการประชุม", detail: "ใช้การแปลแบบพูดตามเป็นช่วง และให้ผู้พูดหยุดเป็นระยะ" },
      { title: "สรุปผล", detail: "ส่งสรุปประเด็นและคำศัพท์ที่ตกลงกันไว้หลังจบงาน" },
    ],
    tipsTh: [
      "ใช้หูฟังมีไมโครโฟนแทนลำโพงตั้งโต๊ะ ลดเสียงสะท้อนและทำให้คำแปลแม่นยำขึ้น",
      "เปิดกล้องเมื่อทำได้ ภาษากายช่วยล่ามจับบริบทได้ดีกว่าการฟังเสียงอย่างเดียว",
    ],
    warningsTh: [
      "หน่วยงานราชการบางแห่งไม่รับล่ามทางไกล ต้องยืนยันก่อนนัดหมาย",
      "อย่าใช้สัญญาณอินเทอร์เน็ตสาธารณะในงานที่มีข้อมูลความลับ",
    ],
    citationTh: "แนวปฏิบัติภายในของสำนักงาน: ทดสอบระบบก่อนงาน และยืนยันการยอมรับล่ามทางไกลกับหน่วยงานผู้รับ",
  },
  {
    slug: "embassy",
    th: "ล่ามสัมภาษณ์สถานทูตและงานกงสุล",
    en: "Embassy & consular interpreting",
    venueTh: "สถานทูต / สถานกงสุล / ศูนย์รับคำร้อง",
    mode: "onsite",
    summaryTh:
      "การสัมภาษณ์วีซ่าหรือการทำเอกสารที่สถานทูตบางแห่งอนุญาตให้มีล่ามร่วมได้ แต่ละคณะผู้แทนมีระเบียบต่างกันทั้งเรื่องการนำล่ามเข้าอาคารและการแสดงเอกสารประจำตัว จึงต้องยืนยันกับสถานทูตก่อนทุกครั้ง ล่ามช่วยเตรียมคำตอบให้ตรงกับเอกสารที่ยื่นไว้แล้ว",
    requirementsTh: [
      "ชื่อสถานทูตและประเภทคำร้อง",
      "สำเนาชุดเอกสารที่ยื่นไว้ เพื่อให้คำตอบสอดคล้องกัน",
      "คำยืนยันจากสถานทูตว่าอนุญาตให้ล่ามเข้าร่วม",
    ],
    stepsTh: [
      { title: "ตรวจระเบียบของสถานทูต", detail: "สอบถามเป็นลายลักษณ์อักษรว่าอนุญาตให้นำล่ามเข้าห้องสัมภาษณ์หรือไม่" },
      { title: "เตรียมชุดเอกสาร", detail: "ทวนเอกสารและคำแปลให้ตรงกับข้อมูลในแบบคำร้อง" },
      { title: "ซ้อมคำถามพื้นฐาน", detail: "ทบทวนคำถามที่พบบ่อยโดยยึดข้อเท็จจริงจริงเท่านั้น" },
      { title: "วันสัมภาษณ์", detail: "ล่ามแปลคำถาม-คำตอบตามจริง ไม่เสริมความ" },
    ],
    tipsTh: [
      "พกสำเนาชุดเอกสารไปด้วยเสมอ แม้ยื่นออนไลน์แล้วก็ตาม",
      "เผื่อเวลาสำหรับการตรวจความปลอดภัยและการฝากอุปกรณ์อิเล็กทรอนิกส์",
    ],
    warningsTh: [
      "อย่าตอบเกินสิ่งที่ถูกถาม และอย่าให้ข้อมูลที่ขัดกับเอกสารที่ยื่น",
      "อย่าคาดหมายผลการพิจารณา — การอนุมัติเป็นดุลพินิจของคณะผู้แทนปลายทาง",
    ],
    citationTh: "ระเบียบการเข้าอาคารและการใช้ล่ามเป็นไปตามที่คณะผู้แทนแต่ละแห่งกำหนด — ยืนยันก่อนวันนัด",
  },
  {
    slug: "land-office",
    th: "ล่ามสำนักงานที่ดินและการโอนอสังหาริมทรัพย์",
    en: "Land Office interpreting",
    venueTh: "สำนักงานที่ดิน",
    mode: "onsite",
    summaryTh:
      "การโอนกรรมสิทธิ์ห้องชุด การจดจำนอง หรือการจดสิทธิเก็บกิน ณ สำนักงานที่ดิน เจ้าพนักงานต้องมั่นใจว่าคู่สัญญาต่างชาติเข้าใจสาระสำคัญของสัญญาที่เป็นภาษาไทย ล่ามจะแปลสัญญาและคำถามของเจ้าพนักงานก่อนลงลายมือชื่อ พร้อมทวนตัวเลขและชื่อผู้ถือกรรมสิทธิ์ให้ตรงเอกสารสิทธิ",
    requirementsTh: [
      "โฉนด/หนังสือกรรมสิทธิ์ห้องชุด และร่างสัญญาที่จะจดทะเบียน",
      "หนังสือเดินทางของผู้ซื้อ/ผู้ขายต่างชาติ",
      "เอกสารประกอบตามประเภทนิติกรรม เช่น หนังสือรับรองการโอนเงินจากต่างประเทศสำหรับห้องชุด",
    ],
    stepsTh: [
      { title: "ตรวจร่างสัญญา", detail: "ส่งร่างให้ล่ามล่วงหน้าเพื่อเตรียมคำแปลและศัพท์เฉพาะทางที่ดิน" },
      { title: "นัดหมายสำนักงานที่ดิน", detail: "ยืนยันวันเวลาและรายการเอกสารกับเจ้าพนักงานผู้รับผิดชอบ" },
      { title: "วันจดทะเบียน", detail: "ล่ามแปลสัญญาและคำถามของเจ้าพนักงานก่อนคู่สัญญาลงลายมือชื่อ" },
      { title: "ตรวจหลังจดทะเบียน", detail: "ทวนรายการสลักหลังเอกสารสิทธิและใบเสร็จค่าธรรมเนียมก่อนออกจากสำนักงาน" },
    ],
    tipsTh: [
      "ทวนการสะกดชื่อผู้ถือกรรมสิทธิ์ให้ตรงหนังสือเดินทางทุกตัวอักษร",
      "ตรวจเลขที่โฉนด เนื้อที่ และภาระผูกพันในเอกสารสิทธิให้ตรงกับสัญญา",
    ],
    warningsTh: [
      "อย่าลงนามในสัญญาภาษาไทยที่ยังไม่ได้ฟังคำแปลครบถ้วน",
      "อย่าถือว่าคำแปลอย่างไม่เป็นทางการมีผลผูกพันแทนฉบับภาษาไทย — ฉบับภาษาไทยคือฉบับที่จดทะเบียน",
    ],
    citationTh:
      "อ้างอิง: ประมวลกฎหมายที่ดิน และพระราชบัญญัติอาคารชุด พ.ศ. 2522 (เงื่อนไขการถือกรรมสิทธิ์ของคนต่างด้าว)",
  },
] as const;

export const INTERPRETER_SCENARIO_BY_SLUG: Record<string, InterpreterScenario> =
  Object.fromEntries(INTERPRETER_SCENARIOS.map((s) => [s.slug, s]));

export const INTERPRETER_TIPS = COMMON_TIPS;
export const INTERPRETER_WARNINGS = COMMON_WARNINGS;

export const INTERPRETER_HOWTO_STEPS: readonly { title: string; detail: string }[] = [
  {
    title: "แจ้งงานและภาษา",
    detail: "ระบุประเภทงาน (ศาล อำเภอ ตม. โรงพยาบาล ธุรกิจ) ภาษา วันเวลา สถานที่ และระยะเวลาโดยประมาณ",
  },
  {
    title: "ยืนยันเงื่อนไขของหน่วยงานผู้รับ",
    detail: "สอบถามหน่วยงานว่ารับล่ามที่จัดหาเองหรือไม่ ต้องแสดงเอกสารประจำตัวล่ามหรือไม่ และรับล่ามทางไกลหรือไม่",
  },
  {
    title: "เตรียมเอกสารและคำศัพท์ล่วงหน้า",
    detail: "ส่งเอกสารที่เกี่ยวข้องให้ล่ามอย่างน้อย 1–2 วันทำการ เพื่อจัดทำรายการคำศัพท์และชื่อเฉพาะ",
  },
  {
    title: "ปฏิบัติงานวันนัด",
    detail: "ล่ามรายงานตัวก่อนเวลา แปลตามที่ผู้พูดสื่อสารจริง และทวนคำแปลของเอกสารก่อนลงลายมือชื่อทุกครั้ง",
  },
  {
    title: "สรุปและงานต่อเนื่อง",
    detail: "สรุปประเด็น รายการเอกสารที่ต้องยื่นเพิ่ม และวางแผนขั้นตอนถัดไป เช่น นิติกรณ์กรมการกงสุลหรือรับรองสถานทูต",
  },
] as const;
